import { validateDate, validateDateTime, validateNumber } from './utils';

/**
 * Generic function to validate and clean object data based on a field configuration
 * @param {object} params - Parameters object
 * @param {object} params.data - Raw data to validate
 * @param {object} params.fieldsConfig - Configuration object defining field requirements and types
 * @param {array} params.requiredFields - Array of required field names
 * @returns {object|null} Validated data or null if required fields are missing
 */
function validateObjectData({ data, fieldsConfig, requiredFields }) {
  if (!data) return null;

  const missingFields = requiredFields.filter(field => !data[field]);
  if (missingFields.length > 0) {
    return null;
  }

  const validatedData = {};

  Object.entries(fieldsConfig).forEach(([field, config]) => {
    const value = data[field];

    if (config.type === 'string') {
      validatedData[field] = value ? String(value) : null;
    } else if (config.type === 'number' || config.type === 'integer') {
      validatedData[field] = validateNumber(value);
    } else if (config.type === 'date') {
      validatedData[field] = validateDate(value);
    } else if (config.type === 'datetime') {
      validatedData[field] = validateDateTime(value);
    } else if (config.type === 'enum' && value) {
      validatedData[field] = config.values.includes(value) ? value : null;
    } else {
      validatedData[field] = value || null;
    }
  });

  Object.keys(validatedData).forEach(key => {
    if (validatedData[key] === null || validatedData[key] === undefined) {
      delete validatedData[key];
    }
  });

  return validatedData;
}

/**
 * Validates and cleans content metadata to match schema requirements
 * @param {object} data - Raw content metadata
 * @returns {object} Validated content metadata
 */
export function validateContentMetadata(data) {
  const fieldsConfig = {
    content_id: { type: 'string' },
    content_title: { type: 'string' },
    slug: { type: 'string' },
    content_format: { type: 'string' },
    content_type: { type: 'string' },
    embedded_content_id: { type: 'string' },
    asset_type: { type: 'string' },
    word_count: { type: 'integer' },
    duration_time: { type: 'number' },
    publish_date: { type: 'date' },
    topic_primary: { type: 'string' },
    topic_secondary: { type: 'string' },
    language: { type: 'string' },
    author: { type: 'string' }
  };

  const requiredFields = ['content_id', 'content_title', 'slug'];

  return validateObjectData({ data, fieldsConfig, requiredFields });
}

/**
 * Validates UI interaction event data
 * @param {object} data - Raw UI interaction data
 * @returns {object|null} Validated UI interaction data or null if invalid
 */
export function validateUiInteractionEvent(data) {
  const fieldsConfig = {
    element_name: { type: 'string' },
    interaction_type: {
      type: 'enum',
      values: ['click', 'tap', 'hover', 'scroll', 'swipe', 'drag', 'long_press']
    },
    element_type: { type: 'string' },
    element_position: { type: 'string' }
  };

  const requiredFields = ['element_name', 'interaction_type'];

  return validateObjectData({ data, fieldsConfig, requiredFields });
}

/**
 * Validates match context data
 * @param {object} data - Raw match context data
 * @returns {object|null} Validated match context data or null if invalid
 */
export function validateMatchContext(data) {
  const fieldsConfig = {
    match_id: { type: 'string' },
    home_team: { type: 'string' },
    away_team: { type: 'string' },
    kickoff_time: { type: 'datetime' },
    home_team_id: { type: 'string' },
    away_team_id: { type: 'string' },
    venue: { type: 'string' },
    match_status: { type: 'string' }
  };

  const requiredFields = ['match_id', 'home_team', 'away_team', 'kickoff_time'];

  return validateObjectData({ data, fieldsConfig, requiredFields });
}

/**
 * Validates club context data
 * @param {object} data - Raw club context data
 * @returns {object|null} Validated club context data or null if invalid
 */
export function validateClubContext(data) {
  const fieldsConfig = {
    club_id: { type: 'string' },
    club_name: { type: 'string' },
    club_city: { type: 'string' },
    club_fanbase_size: { type: 'integer' }
  };

  const requiredFields = ['club_id', 'club_name'];

  return validateObjectData({ data, fieldsConfig, requiredFields });
}

/**
 * Validates player context data
 * @param {object} data - Raw player context data
 * @returns {object|null} Validated player context data or null if invalid
 */
export function validatePlayerContext(data) {
  const fieldsConfig = {
    player_id: { type: 'string' },
    player_name: { type: 'string' },
    player_team: { type: 'string' },
    player_position: { type: 'string' },
    jersey_number: { type: 'integer' }
  };

  const requiredFields = ['player_id', 'player_name'];

  return validateObjectData({ data, fieldsConfig, requiredFields });
}

/**
 * Validates topic context data
 * @param {object} data - Raw topic context data
 * @returns {object|null} Validated topic context data or null if invalid
 */
export function validateTopicContext(data) {
  const fieldsConfig = {
    topic_id: { type: 'string' },
    topic_name: { type: 'string' },
    topic_slug: { type: 'string' },
    topic_type: { type: 'string' }
  };

  const requiredFields = ['topic_id', 'topic_name', 'topic_slug', 'topic_type'];

  return validateObjectData({ data, fieldsConfig, requiredFields });
}